<script setup lang="ts">
const props = defineProps({
  stats: {
    type: Object as PropType<{ [key: string]: Property }>,
    required: true,
  },
  merchantId: {
    type: String,
    required: true,
  },
});

const { locale } = useI18n();
const { getMerchantAttributes } = useLuxuryDesign();
const isOpen = ref(false);
const hasLegacyStats = computed(() => {
  return Object.values(props.stats).some((stat) => stat.type === "LEGACY");
});
const statsSortedByOrder = computed(() => {
  return Object.entries(props.stats)
    .filter(([_, value]) => value.type === "LEGACY")
    .sort((a, b) => a[1].order - b[1].order)
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {} as { [key: string]: Property });
});
const isArrayWithValues = (statValue: Property) => {
  return (
    statValue.valueType === "ARRAY" &&
    statValue.value &&
    Array.isArray(statValue.value)
  );
};
const showModalPropertyList = ref(false);
const modalTitle = ref("");
const modalStatsArray = ref<string[]>();
const openModalPropertyList = (title: string, stats: string[]) => {
  modalTitle.value = title;
  modalStatsArray.value = stats;
  showModalPropertyList.value = true;
};

const { sourcesUrl } = useRuntimeConfig().public;

const legacyAttributes = computed(() =>
  getMerchantAttributes(props.merchantId)
);
</script>
<template>
  <section
    v-bind="legacyAttributes"
    class="card-detail-legacy mt-8">
    <div
      class="w-full bg-neutral-800 text-neutral-200 border border-primary-900"
      :class="isOpen ? 'rounded-t-lg' : 'rounded-lg'">
      <div
        class="py-4 px-6 border-b border-primary-900 flex justify-between"
        @click="isOpen = !isOpen">
        <h1 class="text-xl font-bold">{{ $t("main.storeDetail.legacy") }}</h1>
        <NuxtIcon
          name="mdi:keyboard-arrow-down"
          class="w-7 h-7 transition-all duration-300 cursor-pointer text-neutral-300 shrink-0"
          :class="{ 'transform rotate-180': isOpen }" />
      </div>
    </div>
    <transition
      enter-active-class="transition ease duration-100"
      enter-from-class="transform opacity-0 -translate-y-5"
      enter-to-class="transform opacity-100 translate-y-0"
      leave-active-class="transition ease duration-100"
      leave-from-class="transform opacity-100 translate-y-0"
      leave-to-class="transform opacity-0 -translate-y-5">
      <div
        v-if="isOpen"
        class="w-full grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 justify-center gap-4 border border-primary-900 border-t-0 px-6 py-8 transition-all duration-300 bg-neutral-800 rounded-b-lg">
        <template
          v-if="hasLegacyStats"
          v-for="(statValue, statName) in statsSortedByOrder"
          :key="statName">
          <div class="max-w-full flex flex-col truncate">
            <p id="title" class="text-neutral-300 text-sm truncate">
              {{ $t(`${merchantId}.property.${statName}.title`) }}
            </p>
            <div class="flex gap-1 items-center">
              <p
                id="prefix"
                v-if="
                  statValue.prefixTranslationId &&
                  $te(`${merchantId}.prefix.${statValue.prefixTranslationId}`)
                "
                v-html="
                  $t(`${merchantId}.prefix.${statValue.prefixTranslationId}`, {
                    GFAL_SOURCES_URI: sourcesUrl + '/marketplace',
                  })
                "></p>
              <template v-if="statValue.valueType === 'STRING'">
                <p class="text-neutral-200 text-2xl truncate font-bold">
                  {{
                    $t(
                      `${merchantId}.property.${statName}.values.${statValue.value}`
                    )
                  }}
                </p>
              </template>
              <template v-if="statValue.valueType === 'NUMBER'">
                <p class="text-neutral-200 text-2xl truncate font-bold">
                  {{ statValue.value }}
                </p>
              </template>
              <template v-if="isArrayWithValues(statValue)">
                <p
                  v-if="(statValue.value! as string[]).length < 1"
                  class="text-neutral-200 text-2xl truncate font-bold">
                  0
                </p>
                <p
                  v-else-if="(statValue.value! as string[]).length > 1"
                  class="text-neutral-200 text-2xl truncate font-bold">
                  {{ (statValue.value! as string[]).length }}
                  <span
                    class="text-sm text-neutral-300 cursor-pointer"
                    @click="
                      openModalPropertyList(
                        statName.toString(),
                        statValue.value! as string[]
                      )
                    "
                    >{{ $t("main.storeDetail.clickToView") }}</span
                  >
                </p>
                <p
                  v-else
                  class="text-neutral-200 text-2xl truncate font-bold">
                  {{
                    $te(`${merchantId}.property.${statValue.value?.toString()}`)
                      ? $t(
                          `${merchantId}.property.${statValue.value?.toString()}`
                        )
                      : statValue.value?.toString()
                  }}
                </p>
              </template>
              <template v-if="statValue.valueType === 'TIMESTAMP'">
                <p class="text-neutral-200 text-2xl truncate font-bold">
                  {{
                    new Date(statValue.value as string).toLocaleDateString(
                      locale,
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      }
                    )
                  }}
                </p>
              </template>
              <p
                id="suffix"
                v-if="
                  statValue.suffixTranslationId &&
                  $te(`${merchantId}.suffix.${statValue.suffixTranslationId}`)
                "
                v-html="
                  $t(`${merchantId}.suffix.${statValue.suffixTranslationId}`, {
                    GFAL_SOURCES_URI: sourcesUrl + '/marketplace',
                  })
                "></p>
            </div>
          </div>
        </template>
        <template v-else>
          <p class="text-neutral-300 text-sm">
            {{ $t("main.storeDetail.noData") }}
          </p>
        </template>
      </div>
    </transition>
    <ModalItemProperties
      v-if="showModalPropertyList"
      @close="showModalPropertyList = false">
      <template #header>
        <h1 class="text-xl font-bold">
          {{ $t(`${merchantId}.property.${modalTitle}.title`) }}
        </h1>
      </template>
      <template #content>
        <ul class="flex flex-col gap-y-2 text-neutral-300">
          <template v-for="statName in modalStatsArray">
            <li class="list-disc list-inside">
              {{ $t(`${merchantId}.property.${statName}`) }}
            </li>
          </template>
        </ul>
      </template>
    </ModalItemProperties>
  </section>
</template>
